import { datesEqual } from "./dateCompare";
import { OFF_ATTRIBUTE_VALUE } from "./installerHasFullWeekOff";
import {
  computeRemainingCarpetCapacity,
  dateInJobRange,
} from "./installerCapacityRemaining";
import { computeRemainingHardSurfaceCapacity } from "./hardSurfaceCapacityRemaining";
import { HARD_SURFACE_CAPACITY_TYPES } from "./keys";

// - - - - - - - - - -
// Per-cell decision for issue #140 — "Partial Capacity Availability."
//
// Returns true iff the (installer, dateString) cell should be rendered with
// the red + 🔍-icon treatment. The caller (OptimizerGrid) is responsible for
// applying the styling, mounting the icon, and suppressing the call entirely
// during Time Machine mode (live-only feature — same as #138).
//
// The rule (resolved 2026-05-19 with Brandon via Leland — see #140 paper-trail
// comment for full team discussion):
//
//   1. Cell date must be STRICTLY IN THE FUTURE (date > today). Past cells
//      and today's cell are never highlighted — past is unchangeable, today
//      is too late to act on. This is broader than #138's tomorrow-only
//      scope because Brandon's primary use case (pulling expedite-flagged
//      future-assigned jobs forward) spans multiple future days.
//   2. The installer must NOT have an OFF attribute on that date. Both
//      daily-mode and default-pattern OFF show up in scheduleArray as docs
//      with `attribute === true && encodedSmallTitle === "OFF"`. Mirrors
//      `shouldHighlightEmptyTomorrowCell` from #138.
//   3. The installer must NOT have a compliance-hold marker on that date.
//      Identified by `complianceHold === true` (set in schedule.context.js:1642).
//      Mirrors #138.
//   4. The installer must be Soft Surface (`Scheduling_Surface === "Soft Surface"`)
//      AND have non-zero carpet capacity. Hard-surface and unqualified
//      installers cannot have a carpet partial-capacity opening.
//   5. The installer must have AT LEAST ONE carpet job assigned (statusCode 4,
//      surface === "Carpet") covering this date. Cells with zero carpet
//      assignments are #138's territory (fully-empty); cells with no carpet
//      but other surfaces are out of scope for v1.
//   6. The installer must have REMAINING carpet capacity > 0 on this date.
//      Computed by `computeRemainingCarpetCapacity` using the _once / _twice
//      threshold inferred from carpet-job count.
//
// What this helper INTENTIONALLY does NOT check:
//   • Time Machine mode — caller short-circuits before calling, mirroring
//     #138's `nextBusinessDayDateString = isTimeTraveling ? null : ...` guard.
//   • Whether any recommendations actually exist — the recommender's empty
//     state is shown inside the popover. The red+icon stays on the cell even
//     when the recommender returns no matches; that signals "this is a real
//     gap, even if we can't suggest a filler." The install manager may have
//     external knowledge.
//   • Carpet capacity quality — _twice = 0 + carpetJobCount >= 2 is handled
//     inside computeRemainingCarpetCapacity (clamps remaining to 0 → returns
//     hasPartialCapacity: false). Caller doesn't need to special-case it.

// Local helper — mirrors the inline `new Date(cell) > new Date(today)` pattern
// (no equal). Snaps both sides to local midnight so a time-of-day input
// doesn't accidentally classify today's date as future. Returns false on any
// unparseable input so a malformed value cannot produce false positives.
const isStrictlyFutureDate = (dateString, todayDateString) => {
  if (!dateString || !todayDateString) return false;
  const cellMs = new Date(dateString).setHours(0, 0, 0, 0);
  const todayMs = new Date(todayDateString).setHours(0, 0, 0, 0);
  if (isNaN(cellMs) || isNaN(todayMs)) return false;
  return cellMs > todayMs;
};

/**
 * Per-cell decision for the #140 partial-capacity red+icon highlight.
 *
 * @param {Object} params
 * @param {string} params.dateString          — cell date "M/D/YYYY"
 * @param {string} params.todayDateString     — today's local date in
 *                                              "M/D/YYYY" — caller passes
 *                                              `new Date().toLocaleDateString("en-us")`.
 *                                              Injected as a parameter (vs
 *                                              `new Date()` inside) so tests
 *                                              can freeze time without mocks.
 * @param {Object} params.candidate           — installer Candidate doc
 * @param {Array}  params.scheduleArray       — projected scheduleArray for the
 *                                              week (attribute + job tiles);
 *                                              used for OFF / compliance-hold
 *                                              detection.
 * @param {Array}  params.activeQueueDocs     — raw ActiveQueue docs (for
 *                                              capacity math).
 * @param {string} params.locationFilter      — current location filter; used
 *                                              to scope the OFF/compliance
 *                                              check (mirrors #138).
 * @param {Array}  [params.furnitureSkuList]   — SKU list for furniture line
 *                                              items (JOB_FURNITURE_SKULIST).
 *                                              Forwarded to the capacity calc
 *                                              so capacityInfo carries the
 *                                              furniture-remaining numbers
 *                                              for popover display.
 * @param {Array}  [params.stepsSkuList]       — SKU list for steps line items
 *                                              (JOB_STEPS_SKULIST). Same
 *                                              forwarding as furniture.
 * @param {Function} [params.normalizePayroll] — optional payroll formatter.
 *
 * @returns {Object} {
 *   shouldHighlight,             — boolean: render the red outline + 🔍 icon
 *   capacityInfo,                — the full computeRemainingCarpetCapacity
 *                                  output (capacityOnce, capacityTwice,
 *                                  effectiveCap, carpetJobCount, usedYds,
 *                                  remaining). Always returned even when
 *                                  shouldHighlight is false, so the caller
 *                                  can avoid recomputing for the popover.
 * }
 */
export function shouldHighlightPartialCapacityCell({
  dateString,
  todayDateString,
  candidate,
  scheduleArray,
  activeQueueDocs,
  locationFilter,
  furnitureSkuList,
  stepsSkuList,
  normalizePayroll,
}) {
  const empty = {
    shouldHighlight: false,
    capacityInfo: {
      capacityOnce: 0,
      capacityTwice: 0,
      effectiveCap: 0,
      capacityOnceFurniture: 0,
      capacityTwiceFurniture: 0,
      effectiveFurnitureCap: 0,
      capacityOnceSteps: 0,
      capacityTwiceSteps: 0,
      effectiveStepsCap: 0,
      carpetJobCount: 0,
      usedYds: 0,
      usedFurniture: 0,
      usedSteps: 0,
      remaining: 0,
      remainingFurniture: 0,
      remainingSteps: 0,
      hasPartialCapacity: false,
      isSoftSurfaceInstaller: false,
    },
  };

  if (!dateString || !candidate || !Array.isArray(scheduleArray)) {
    return empty;
  }

  // Gate 1: strictly future cells only.
  if (!isStrictlyFutureDate(dateString, todayDateString)) return empty;

  // Gate 2 + 3: OFF or compliance hold suppresses the highlight. Mirror #138's
  // logic exactly so the two highlights stay in lockstep about who is
  // "unavailable" on a given day.
  const normalize = normalizePayroll || ((v) => v);
  const installerPayroll = normalize(candidate.payrollNo);
  const isUnavailable = scheduleArray.some(
    (doc) =>
      doc &&
      doc.attribute === true &&
      normalize(doc.installer) === installerPayroll &&
      doc.location === locationFilter &&
      (doc.encodedSmallTitle === OFF_ATTRIBUTE_VALUE ||
        doc.complianceHold === true) &&
      datesEqual(doc.start, dateString)
  );
  if (isUnavailable) return empty;

  // Gate 3.5 — SHARED-cell suppression (Brandon UAT bug 2 + #153, 2026-05-21).
  // When an installer is shared OUT to another location on this date (i.e.
  // their assigned job's location !== current view's locationFilter), the
  // optimizer renders the cell as "SHARED <LOC>". The installer is already
  // committed elsewhere that day; partial-capacity recommendations must NOT
  // appear on SHARED cells. Mirrors the optimizerScreen.js logic that flips
  // the JobTile into "SHARED <LOC>" mode for cross-location assignments.
  //
  // 2026-05-21 (#153) — uses `dateInJobRange(cellDate, date_scheduled,
  // date_estimatedCompletion)` instead of single-date `datesEqual` so
  // multi-day jobs spanning across the cell date are detected too. Brandon's
  // 5/27 example: installer 1990.01 has inv 2498626 (Vasquez) assigned at
  // AUG with date_scheduled=5/26 and date_estimatedCompletion=5/27 — a
  // two-day job. The capacity helper counts this toward usedJobStops on
  // BOTH 5/26 AND 5/27 (via the same dateInJobRange call), so partial-cap
  // fires on 5/27 — but the prior gate using `datesEqual(date_scheduled,
  // 5/27)` returned false (date_scheduled is 5/26), so SHARED suppression
  // didn't kick in. Aligning both helpers to the same multi-day awareness
  // closes that gap.
  if (locationFilter && Array.isArray(activeQueueDocs)) {
    const isSharedToAnotherLocation = activeQueueDocs.some(
      (doc) =>
        doc &&
        normalize(doc.installer) === installerPayroll &&
        dateInJobRange(dateString, doc.date_scheduled, doc.date_estimatedCompletion) &&
        doc.location &&
        doc.location !== locationFilter
    );
    if (isSharedToAnotherLocation) return empty;
  }

  // Gates 4 + 5 + 6: delegate to the capacity helper(s).
  //
  // #175 (Brandon, 2026-06-03) — surface routing is driven by the installer's
  // CONFIGURED capacities, NOT by the Scheduling_Surface classification.
  //
  // Why config-driven, not classification-driven: UAT data on 2026-06-03 shows
  // 156 "does-both" installers (non-zero carpet caps AND non-zero hard-surface
  // caps), and MANY of them are classified "Soft Surface" — including the named
  // example Sergio Osorio (1569.01, Soft Surface). The earlier #173 routing
  // keyed off `Scheduling_Surface === "Hard Surface"`, so those Soft-Surface-
  // classified does-both installers only ever saw the carpet block and never
  // their hard-surface capacity. Routing off the caps fixes that for everyone.
  //
  // Three outcomes:
  //   • carpet caps only  → carpet capacityInfo (isHardSurface falsy)
  //   • hard caps only    → hard capacityInfo   (isHardSurface true)   — fixes
  //                         0606.01's blank-carpet bug, #175 core
  //   • BOTH configured   → combined { isBothSurfaces, carpetInfo, hardInfo }
  //                         (Option B): popover shows both summary sections and
  //                         the icon runs + merges both recommenders.
  //
  // Both engines expose the same hasPartialCapacity (icon) +
  // hasYardagePartialCapacity (border) flags; for the combined case we OR them
  // so OptimizerGrid's border/icon decision is unchanged.
  const positiveCap = (v) => {
    const n = parseFloat(v);
    return !isNaN(n) && n > 0;
  };
  const hasCarpetCaps =
    positiveCap(candidate.capacity_carpet_once) ||
    positiveCap(candidate.capacity_carpet_twice);
  const hasHardCaps = HARD_SURFACE_CAPACITY_TYPES.some(
    (t) =>
      positiveCap(candidate[`capacity_${t.capacityField}_once`]) ||
      positiveCap(candidate[`capacity_${t.capacityField}_twice`])
  );

  const hardInfo = hasHardCaps
    ? computeRemainingHardSurfaceCapacity({
        candidate,
        dateString,
        activeQueueDocs,
        normalizePayroll,
      })
    : null;
  const carpetInfo = hasCarpetCaps
    ? computeRemainingCarpetCapacity({
        candidate,
        dateString,
        activeQueueDocs,
        furnitureSkuList,
        stepsSkuList,
        normalizePayroll,
      })
    : null;

  // Both surfaces configured → combined capacityInfo (Option B).
  if (hardInfo && carpetInfo) {
    return {
      shouldHighlight:
        hardInfo.hasPartialCapacity || carpetInfo.hasPartialCapacity,
      capacityInfo: {
        isBothSurfaces: true,
        carpetInfo,
        hardInfo,
        hasPartialCapacity:
          !!carpetInfo.hasPartialCapacity || !!hardInfo.hasPartialCapacity,
        hasYardagePartialCapacity:
          !!carpetInfo.hasYardagePartialCapacity ||
          !!hardInfo.hasYardagePartialCapacity,
      },
    };
  }

  // Hard-surface only.
  if (hardInfo) {
    return {
      shouldHighlight: hardInfo.hasPartialCapacity,
      capacityInfo: hardInfo,
    };
  }

  // Carpet only (or neither — the carpet engine returns a sane zero-state
  // empty for an installer with no carpet caps, hasPartialCapacity:false). The
  // soft-surface requirement, once/twice threshold, _twice=0 clamp, and the
  // carpetJobCount + remaining + #171 job-stop gates are all encoded inside the
  // helper, so we just read `hasPartialCapacity`.
  const info =
    carpetInfo ||
    computeRemainingCarpetCapacity({
      candidate,
      dateString,
      activeQueueDocs,
      furnitureSkuList,
      stepsSkuList,
      normalizePayroll,
    });
  return {
    shouldHighlight: info.hasPartialCapacity,
    capacityInfo: info,
  };
}
